<section class="page">
  <header class="page-header">
    <h2>Lancer les analyses</h2>
    <p class="subtitle">Gérer et lancer les analyses des fichiers importés.</p>
  </header>

  <section class="panel">
    <div class="search-box">
      <input
        type="text"
        placeholder="Filtrer par fichier, cartouche, département, n° commande ou identifiant client..."
        [(ngModel)]="searchFilter"
        (ngModelChange)="onSearchFilterChange()"
      />
    </div>

    @if (!loading) {
      <p class="search-summary">
        @if (searchFilter.trim()) {
          {{ filteredFiles.length }} résultat(s) sur {{ files.length }} fichier(s)
        } @else {
          {{ files.length }} fichier(s)
        }
      </p>
    }

    @if (loading) {
      <p class="meta">Chargement des fichiers...</p>
    } @else if (error) {
      <p class="error">{{ error }}</p>
    }

    @if (success) {
      <p class="success">{{ success }}</p>
    }

    <fieldset class="result-files-fieldset">
      <legend>Fichiers Excel du pipe Antagène</legend>
      @if (resultFiles.length > 0) {
        <div class="result-files-actions">
          <button
            type="button"
            class="pipe-action-btn"
            [disabled]="downloadingResultZip"
            (click)="downloadAllResultFilesZip()"
          >
            {{ downloadingResultZip ? 'Préparation du ZIP...' : 'Tout télécharger (ZIP)' }}
          </button>
        </div>
        <div class="result-files-links">
          @for (resultFile of resultFiles; track resultFile) {
            <button
              type="button"
              class="result-file-link"
              (click)="downloadFile(resultFile)"
            >
              {{ resultFile }}
            </button>
          }
        </div>
      } @else {
        <p class="meta">Aucun fichier Excel généré pour cette exécution.</p>
      }
    </fieldset>

    @if (!loading && files.length === 0) {
      <p class="meta">Aucun fichier trouvé.</p>
    } @else if (!loading && filteredFiles.length === 0) {
      <p class="meta">Aucun fichier ne correspond à votre recherche.</p>
    } @else if (!loading) {
      <div class="pipe-actions-bar">
          <div class="pipe-mode-input">
            <label>Mode croisement</label>
            <button
              type="button"
              class="pipe-mode-option"
              [class.active]="croisementMode === 'pln_tb'"
              (click)="setCroisementMode('pln_tb')"
            >
              PLN+TB
            </button>
            <button
              type="button"
              class="pipe-mode-option"
              [class.active]="croisementMode === 'pln'"
              (click)="setCroisementMode('pln')"
            >
              PLN
            </button>
            <button
              type="button"
              class="pipe-mode-option"
              [class.active]="croisementMode === 'tb'"
              (click)="setCroisementMode('tb')"
            >
              TB
            </button>
          </div>
          <div class="pipe-mode-input">
            <label for="departements-tsv">Fichier TSV départements (optionnel)</label>
            <input
              id="departements-tsv"
              type="file"
              accept=".tsv,text/tab-separated-values,text/plain"
              (change)="onDepartementsTsvSelected($event)"
            />
            @if (departementsTsvFile) {
              <span class="meta">{{ departementsTsvFile.name }}</span>
            }
          </div>
          <button
            type="button"
            class="pipe-action-btn"
            [disabled]="creatingChorologie"
            (click)="createChorologieTxt()"
            title="Exécute PLN puis TB puis TB+PLN"
          >
            {{ creatingChorologie ? 'Création chorologie...' : 'Créer chorologie.txt' }}
          </button>
          <button
            type="button"
            class="pipe-action-btn"
            [disabled]="launchingAntagene"
            (click)="launchPipeAntagene()"
            title="Crée input_pipe.csv"
          >
          Pipe Antagène
        </button>
        <button type="button" class="pipe-action-btn" disabled title="Lancer le pipe Consensus (non disponible)">
          Pipe Consensus
        </button>
      </div>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Fichier de départ</th>
              <th>Taille</th>
              <th>Date d'importation</th>
              <th>Cartouches liées</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            @for (file of paginatedFiles; track file.name) {
              <tr>
                <td>
                  <button
                    type="button"
                    class="file-title-link"
                    (click)="downloadFile(file.name)"
                    title="Télécharger le fichier"
                  >
                    {{ file.name }}
                  </button>
                </td>
                <td>{{ (file.size / 1024).toFixed(2) }} KB</td>
                <td>{{ formatDateFR(file.date_import) }}</td>
                <td>
                  @if (file.cartoucheLines.length === 0) {
                    —
                  } @else {
                    <label class="cartouche-line cartouche-line-select-all">
                      <input
                        type="checkbox"
                        [ngModel]="areAllCartouchesChecked(file)"
                        (ngModelChange)="toggleAllCartouchesForFile(file, $event)"
                      />
                      <span>Tout cocher</span>
                    </label>
                    <div class="cartouche-lines">
                      @for (line of file.cartoucheLines; track line.nom) {
                        <label class="cartouche-line" [title]="line.commandeClientLabel">
                          <input type="checkbox" [(ngModel)]="line.checked" />
                          <span>{{ line.nom }}</span>
                          @if (line.departementLabel) {
                            <span class="cartouche-line-meta">({{ line.departementLabel }})</span>
                          }
                        </label>
                      }
                    </div>
                  }
                </td>
                <td class="actions">
                  <button
                    type="button"
                    class="btn-delete"
                    [disabled]="deletingByName[file.name]"
                    (click)="deleteFile(file.name)"
                    title="Supprimer le fichier"
                  >
                    {{ deletingByName[file.name] ? 'Suppression...' : 'Supprimer' }}
                  </button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <div class="pagination">
        <button
          type="button"
          [disabled]="currentPage <= 1"
          (click)="previousPage()"
        >
          ← Précédent
        </button>
        <span class="page-info">
          Page {{ currentPage }} sur {{ totalPages }}
        </span>
        <button
          type="button"
          [disabled]="currentPage >= totalPages"
          (click)="nextPage()"
        >
          Suivant →
        </button>
      </div>

      @if (debugLogs.length > 0) {
        <section class="debug-panel">
          <h3>Diagnostics import Antagène</h3>
          <div class="debug-lines">
            @for (log of debugLogs; track $index) {
              <p>{{ log }}</p>
            }
          </div>
        </section>
      }
    }
  </section>
</section>
